import React, { useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isSameDay,
  isToday,
  isFuture,
  subMonths,
  addMonths,
} from "date-fns";

interface DatePickerProps {
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  onClose: () => void;
}

export default function DatePicker({
  selectedDate,
  onSelect,
  onClose,
}: DatePickerProps) {
  const [viewMonth, setViewMonth] = useState(selectedDate || new Date());

  const start = startOfMonth(viewMonth);
  const end = endOfMonth(viewMonth);
  const days = eachDayOfInterval({ start, end });

  const startPad = getDay(start);
  const paddedDays: (Date | null)[] = [...Array(startPad).fill(null), ...days];

  const handleSelect = (date: Date) => {
    if (isFuture(date) && !isToday(date)) return;
    onSelect(date);
    onClose();
  };

  return (
    <div
      className="absolute top-full left-0 z-50 bg-white rounded-xl shadow-xl border border-gray-100 p-3 mt-1"
      style={{ width: "260px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex gap-1.5 mb-3">
        <button
          className="flex-1 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-700 transition-colors"
          onClick={() => {
            onSelect(new Date());
            onClose();
          }}
        >
          T Today
        </button>
        <button
          className="flex-1 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 transition-colors"
          onClick={() => {
            const y = new Date();
            y.setDate(y.getDate() - 1);
            onSelect(y);
            onClose();
          }}
        >
          Y Yesterday
        </button>
      </div>

      <div className="flex items-center justify-between mb-2">
        <button
          className="p-1 rounded hover:bg-gray-100 text-gray-500 text-sm transition-colors"
          onClick={() => setViewMonth((m) => subMonths(m, 1))}
        >
          ‹
        </button>
        <span className="text-xs font-semibold text-gray-700">
          {format(viewMonth, "MMMM yyyy")}
        </span>
        <button
          className="p-1 rounded hover:bg-gray-100 text-gray-500 text-sm transition-colors"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          disabled={
            isSameDay(endOfMonth(viewMonth), endOfMonth(new Date())) ||
            isFuture(endOfMonth(viewMonth))
          }
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div
            key={d}
            className="text-center text-[10px] font-semibold text-gray-300 py-1"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0.5">
        {paddedDays.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} />;
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const todayDate = isToday(day);
          const disabled = isFuture(day) && !isToday(day);
          return (
            <button
              key={day.toISOString()}
              disabled={disabled}
              onClick={() => handleSelect(day)}
              className={`
                h-8 w-full rounded-lg text-xs font-medium transition-all
                ${isSelected ? "bg-gray-900 text-white" : ""}
                ${todayDate && !isSelected ? "text-blue-500 font-bold" : ""}
                ${disabled ? "text-gray-200 cursor-not-allowed" : !isSelected ? "hover:bg-gray-100 text-gray-700 cursor-pointer" : ""}
              `}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
}
